import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(annotationPlugin);

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useEffect, useMemo, useRef } from "react";
import moment from "moment/moment";

let lockedMax;

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);
// Decide bucket size based on visible range
function chooseTimelineSteps(rangeMs) {
    const minute = 60_000;
    const hour = 60 * minute;
    const day = 24 * hour;

    // baseStep = how many minutes between tick marks
    // majorEvery = how many minutes between labeled ticks
    if (rangeMs <= 2 * hour) return { baseStep: 1, majorEvery: 30 };  // tick each minute, label 15s
    if (rangeMs <= 12 * hour) return { baseStep: 5, majorEvery: 15 };  // tick 5m, label 15s
    if (rangeMs <= 2 * day) return { baseStep: 15, majorEvery: 60 };  // tick 15m, label hourly
    if (rangeMs <= 14 * day) return { baseStep: 60, majorEvery: 240 }; // tick hourly, label 4-hourly
    return { baseStep: 240, majorEvery: 1440 };      // tick 4h, label daily
}
const timelineTicksPlugin = {
    id: "timelineTicks",
    afterDraw(chart, args, opts) {
        const x = chart.scales.x;
        if (!x) return;

        const { ctx, chartArea } = chart;
        const y0 = chartArea.top;

        const baseStepMin = opts?.baseStepMin ?? 5;
        const majorEveryMin = opts?.majorEveryMin ?? 15;

        const minorLen = opts?.minorLen ?? 4;
        const majorLen = opts?.majorLen ?? 10;
        const labelOffset = opts?.labelOffset ?? 14;
        const minLabelGapPx = opts?.minLabelGapPx ?? 55;

        const min = x.min;
        const max = x.max;
        if (min == null || max == null) return;

        const minuteMs = 60_000;

        const start = new Date(min);
        start.setSeconds(0, 0);
        start.setMinutes(
            Math.floor(start.getMinutes() / baseStepMin) * baseStepMin
        );

        // Build all major tick times first
        const majorTicks = [];
        for (let t = start.getTime(); t <= max; t += baseStepMin * minuteMs) {
            const d = new Date(t);
            if (d.getMinutes() % majorEveryMin === 0) {
                const px = x.getPixelForValue(t);
                if (px >= chartArea.left && px <= chartArea.right) {
                    majorTicks.push({ t, px, d });
                }
            }
        }

        // If labels are too close together, skip some
        let labelEvery = 1;
        if (majorTicks.length >= 2) {
            const avgGap =
                (majorTicks[majorTicks.length - 1].px - majorTicks[0].px) /
                (majorTicks.length - 1);

            labelEvery = Math.max(1, Math.ceil(minLabelGapPx / avgGap));
        }

        ctx.save();

        const extraTop = opts?.clipExtraTop ?? 40;
        ctx.beginPath();
        ctx.rect(
            chartArea.left,
            chartArea.top - extraTop,
            chartArea.right - chartArea.left,
            (chartArea.bottom - chartArea.top) + extraTop
        );
        ctx.clip();

        ctx.strokeStyle = opts?.strokeStyle ?? "rgba(0,0,0,0.35)";
        ctx.fillStyle = opts?.fillStyle ?? "rgba(0,0,0,0.6)";
        ctx.font = opts?.font ?? "11px system-ui";
        ctx.lineWidth = 1;
        ctx.textBaseline = "bottom";

        let majorIndex = 0;

        for (let t = start.getTime(); t <= max; t += baseStepMin * minuteMs) {
            const d = new Date(t);
            const mins = d.getMinutes();
            const isMajor = mins % majorEveryMin === 0;

            const px = x.getPixelForValue(t);
            if (px < chartArea.left || px > chartArea.right) continue;

            const len = isMajor ? majorLen : minorLen;

            // tick mark
            ctx.beginPath();
            ctx.moveTo(px, y0);
            ctx.lineTo(px, y0 + len);
            ctx.stroke();

            // label only every Nth major tick
            if (isMajor) {
                if (majorIndex % labelEvery === 0) {
                    const hh = String(d.getHours()).padStart(2, "0");
                    const mm = String(d.getMinutes()).padStart(2, "0");
                    const label = `${hh}:${mm}`;
                    const w = ctx.measureText(label).width;
                    ctx.fillText(label, px - w / 2, y0 - labelOffset);
                }
                majorIndex++;
            }
        }

        ctx.restore();
    }
};

const reserveTimelineHeightPlugin = {
    id: "reserveTimelineHeight",
    afterFit(scale, args, opts) {
        // only apply to x scale
        if (scale.id !== "x") return;

        const extra = opts?.extra ?? 24; // px reserved under the axis
        scale.height += extra;
    }
};

ChartJS.register(timelineTicksPlugin);
// ChartJS.register(reserveTimelineHeightPlugin);

var timeout, current_chart;
var scroll_event_happening = false;

export default function BaseLineChart({

    title,
    datasets,
    startTime,
    endTime,
    startLimit,
    endLimit,
    timestamps,
    updateTimestamp,
    onTimelineChange,
    fillType,
    stacked,
    highlightEpoch,
    highlightDuration,
    lockToEndLimit = false
}) {

    const wheelRef = useRef(null);
    const chartRef = useRef(null);



    const runWheelScrollEvent = (chart) => {
        var start_time, end_time, group_size;
        let min_limit = moment(0)
        let max_limit = moment();
        // console.log(chart.scales.x.min)
        let min_moment = moment(chart.scales.x.min);
        let max_moment = moment(chart.scales.x.max);

        if (min_moment.isBefore(min_limit)) {
            start_time = min_limit;
        }
        else {
            start_time = min_moment;
        }

        if (max_moment.isAfter(max_limit)) {
            end_time = max_limit;
        }
        else {
            end_time = max_moment;
        }

        setTimeout(function () {
            onTimelineChange({ start: start_time.unix(), end: end_time.unix() })
        })


    };

    function chooseMajorEvery(rangeMs) {
        const minute = 60 * 1000;
        const hour = 60 * minute;
        const day = 24 * hour;

        if (rangeMs <= 2 * hour) return 1;   // label every minute
        if (rangeMs <= 12 * hour) return 5;  // every 5
        if (rangeMs <= 2 * day) return 15;   // every 15
        return 60;                            // every hour
    }


    const scrollEvent = (chart) => {
        // console.log(timeout);
        current_chart = chart;
        if (typeof timeout != 'undefined') {
            clearTimeout(timeout);
            timeout = undefined;
        }

        timeout = setTimeout(() => {

            runWheelScrollEvent(current_chart);

            clearTimeout(timeout)
            timeout = undefined;
            scroll_event_happening = false;
        }, 400);
    }

    function findClosestEpoch(targetEpoch, epochs) {
        if (!epochs.length) return null;

        return epochs.reduce((closest, current) =>
            Math.abs(current - targetEpoch) < Math.abs(closest - targetEpoch)
                ? current
                : closest
        );
    }

    const data = { labels: timestamps.map(r => r * 1000), datasets: datasets };


    const options = useMemo(() => {
        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            // Use the none mode for performance during rapid updates
            animation: false,
            onHover: (evt, elements, chart) => {
                if (!chart) return;
                const xScale = chart.scales.x;
                if (!xScale) return;

                const epoch = xScale.getValueForPixel(evt.x);
                if (epoch != null) {
                    // Snapping logic moved to utils
                    updateTimestamp?.(findClosestEpoch(epoch / 1000, timestamps));
                }
            },
            interaction: { mode: "index", intersect: false },
            layout: {
                padding: { bottom: 0, top: 30 }
            },
            plugins: {
                timelineTicks: {
                    majorEvery: 15,
                    minorLen: 4,
                    majorLen: 10
                },
                reserveTimelineHeight: { extra: 100 },
                legend: { display: false },
                zoom: {
                    zoom: {
                        mode: 'x',
                        drag: { enabled: false },
                        wheel: { enabled: true },
                        pinch: { enabled: false },
                        speed: 0.5,
                        sensitivity: 0.5,
                        onZoomComplete({ chart }) {
                            scrollEvent(chart);
                            // const x = chart.scales.x;
                            // const rangeMs = x.max - x.min;
                            // const { baseStep, majorEvery } = chooseTimelineSteps(rangeMs);

                            // chart.options.scales.x.time.unit = "minute";
                            // chart.options.scales.x.time.stepSize = baseStep;
                            // chart.options.plugins.timelineTicks.majorEvery = majorEvery;
                            chart.update("none");
                        },
                        onZoomStart: ({ chart }) => {
                            lockedMax = chart.scales.x.max;
                        },
                        onZoom: function ({ chart }) {
                            scroll_event_happening = true;
                            if (lockToEndLimit) {
                                chart.options.scales.x.max = lockedMax;
                            }
                            chart.update('none');
                        }
                    },
                    limits: {
                        x: {
                            min: startLimit * 1000,
                            max: endLimit * 1000,
                            minRange: 1000,
                        }
                    }
                },
                tooltip: { enabled: true }
            },
            scales: {
                x: {
                    min: startTime * 1000,
                    max: endTime * 1000,
                    type: "time",
                    ticks: { display: false, autoSkip: true },
                    grid: { drawTicks: false, drawOnChartArea: false },
                    afterFit: (scale) => {
                        scale.paddingTop = (scale.paddingTop || 0) + 36;
                    },
                    border: {
                        display: true,
                        color: "rgba(0,0,0,0.3)",
                        width: 1
                    },
                },
                y: {
                    beginAtZero: false,
                    stacked: stacked // Reactive stacking
                }
            }
        };

        // Add the Table Hover Highlight
        if (highlightEpoch && highlightDuration) {
            chartOptions.plugins.annotation = {
                annotations: {
                    highlightZone: {
                        type: 'box',
                        drawTime: 'beforeDatasetsDraw',
                        xScaleID: 'x',
                        xMin: highlightEpoch * 1000,
                        xMax: (highlightEpoch + highlightDuration) * 1000,
                        backgroundColor: 'rgba(128, 128, 128, 0.15)',
                        borderWidth: 0,
                    }
                }
            };
        }

        return chartOptions;
    }, [
        startTime,
        endTime,
        startLimit,
        endLimit,
        stacked,
        highlightEpoch,
        highlightDuration,
        lockToEndLimit
    ]);

    return (

        <Line ref={chartRef} data={data} options={options} />

    );
}
